import React, { useState, useEffect, useCallback } from "react";
import Sidebar from "../components/Sidebar";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "http://localhost:5002";

const CATEGORIES = [
  "Groceries", "Dining", "Retail", "Household", "Transportation", 
  "Utilities", "Entertainment", "Travel", "Rent", "Other"
];

const EMPTY_FORM = {
  item: "", date: new Date().toISOString().split("T")[0],
  store: "", category: "", price: "",
  quantity: "", unit: "", notes: ""
};

export default function Expenses({ user, onNavigate }) {
  const [expenses, setExpenses]         = useState([]);
  const [loading, setLoading]           = useState(true);
  const [showForm, setShowForm]         = useState(false);
  const [form, setForm]                 = useState(EMPTY_FORM);
  const [editId, setEditId]             = useState(null);
  const [editForm, setEditForm]         = useState(null);
  const [errors, setErrors]             = useState({});
  const [filterText, setFilterText]     = useState("");
  const [filterCategory, setFilterCategory] = useState("");
  const [saving, setSaving]             = useState(false);
  const [sortKey, setSortKey]           = useState("date");
  const [sortDir, setSortDir]           = useState("desc");

  const fetchExpenses = useCallback(() => {
    setLoading(true);
    fetch(`${BACKEND}/expenses`, { credentials: "include" })
      .then(r => r.json())
      .then(data => {
        setExpenses(data);
        setLoading(false);
      });
  }, []);

  useEffect(() => { fetchExpenses(); }, [fetchExpenses]);

  // ── Filtering ──
  const filtered = expenses.filter(e => {
    const matchText = filterText === "" ||
      e.item?.toLowerCase().includes(filterText.toLowerCase()) ||
      e.store?.toLowerCase().includes(filterText.toLowerCase()) ||
      e.notes?.toLowerCase().includes(filterText.toLowerCase());
    const matchCat = filterCategory === "" || e.category === filterCategory;
    return matchText && matchCat;
  });

  // ── Sorting ──
  function handleSort(key) {
    if (sortKey === key) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortKey(key); setSortDir("asc"); }
  }

  const sorted = [...filtered].sort((a, b) => {
    let aVal = a[sortKey];
    let bVal = b[sortKey];

    if (sortKey === "price") {
      aVal = parseFloat(aVal || 0);
      bVal = parseFloat(bVal || 0);
    } else if (sortKey === "date") {
      aVal = new Date(aVal);
      bVal = new Date(bVal);
    } else {
      aVal = (aVal || "").toString().toLowerCase();
      bVal = (bVal || "").toString().toLowerCase();
    }

    if (aVal < bVal) return sortDir === "asc" ? -1 : 1;
    if (aVal > bVal) return sortDir === "asc" ? 1 : -1;
    return 0;
  });

  // ── Validation ──
  function validate(f) {
    const e = {};
    if (!f.item.trim()) e.item = true;
    if (!f.date)        e.date = true;
    if (!f.price || isNaN(parseFloat(f.price))) e.price = true;
    return e;
  }

  // ── Add ──
  async function handleAdd(e) {
    e.preventDefault();
    const errs = validate(form);
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaving(true);
    await fetch(`${BACKEND}/expenses`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(form)
    });
    setForm(EMPTY_FORM);
    setShowForm(false);
    setErrors({});
    setSaving(false);
    fetchExpenses();
  }

  // ── Delete ──
  async function handleDelete(id) {
    if (!window.confirm("Delete this expense?")) return;
    await fetch(`${BACKEND}/expenses/${id}`, {
      method: "DELETE",
      credentials: "include"
    });
    fetchExpenses();
  }

  // ── Edit ──
  function startEdit(expense) {
    setEditId(expense.id);
    setEditForm({
      item:     expense.item     || "",
      date:     expense.date?.split("T")[0] || "",
      store:    expense.store    || "",
      category: expense.category || "",
      price:    expense.price    || "",
      quantity: expense.quantity || "",
      unit:     expense.unit     || "",
      notes:    expense.notes    || "",
    });
  }

  async function handleEditSave(id) {
    const errs = validate(editForm);
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSaving(true);
    await fetch(`${BACKEND}/expenses/${id}`, {
      method: "PUT",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body: JSON.stringify(editForm)
    });
    setEditId(null);
    setEditForm(null);
    setErrors({});
    setSaving(false);
    fetchExpenses();
  }

  function handleFormChange(f, key, val) {
    if (f === "add") setForm(prev => ({ ...prev, [key]: val }));
    else setEditForm(prev => ({ ...prev, [key]: val }));
    setErrors(prev => ({ ...prev, [key]: false }));
  }

  return (
    <div className="dashboard-layout">
      <Sidebar user={user} active="expenses" onNavigate={onNavigate} />

      <main className="dashboard-main">
        {/* Header */}
        <div className="expenses-header">
          <div>
            <div className="expenses-title">Expenses</div>
            <div className="expenses-subtitle">{expenses.length} total expenses</div>
          </div>
          <button
            className="btn-primary"
            onClick={() => { setShowForm(f => !f); setErrors({}); }}
          >
            {showForm ? "✕ Cancel" : "+ Add Expense"}
          </button>
        </div>

        {/* Add Form */}
        {showForm && (
          <div className="add-form-card">
            <div className="add-form-title">New Expense</div>
            <form onSubmit={handleAdd}>
              <div className="add-form-grid">
                <div className="form-field">
                  <label className="form-label">Item *</label>
                  <input
                    className={`form-input ${errors.item ? "error" : ""}`}
                    placeholder="e.g. Oat Milk"
                    value={form.item}
                    onChange={ev => handleFormChange("add", "item", ev.target.value)}
                  />
                </div>
                <div className="form-field">
                  <label className="form-label">Date *</label>
                  <input
                    type="date"
                    className={`form-input ${errors.date ? "error" : ""}`}
                    value={form.date}
                    onChange={ev => handleFormChange("add", "date", ev.target.value)}
                  />
                </div>
                <div className="form-field">
                  <label className="form-label">Store</label>
                  <input
                    className="form-input"
                    placeholder="e.g. Costco"
                    value={form.store}
                    onChange={ev => handleFormChange("add", "store", ev.target.value)}
                  />
                </div>
                <div className="form-field">
                  <label className="form-label">Category</label>
                  <select
                    className="form-input"
                    value={form.category}
                    onChange={ev => handleFormChange("add", "category", ev.target.value)}
                  >
                    <option value="">Select…</option>
                    {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                  </select>
                </div>
              </div>

              <div className="add-form-grid-bottom">
                <div className="form-field">
                  <label className="form-label">Notes</label>
                  <input
                    className="form-input"
                    placeholder="Optional notes"
                    value={form.notes}
                    onChange={ev => handleFormChange("add", "notes", ev.target.value)}
                  />
                </div>
                <div className="form-field">
                  <label className="form-label">Qty + Unit</label>
                  <div style={{ display: "flex", gap: "6px" }}>
                    <input
                      className="form-input"
                      placeholder="1"
                      style={{ width: "60px" }}
                      value={form.quantity}
                      onChange={ev => handleFormChange("add", "quantity", ev.target.value)}
                    />
                    <input
                      className="form-input"
                      placeholder="kg"
                      value={form.unit}
                      onChange={ev => handleFormChange("add", "unit", ev.target.value)}
                    />
                  </div>
                </div>
                <div className="form-field">
                  <label className="form-label">Price * ($)</label>
                  <input
                    className={`form-input ${errors.price ? "error" : ""}`}
                    placeholder="0.00"
                    value={form.price}
                    onChange={ev => handleFormChange("add", "price", ev.target.value)}
                  />
                </div>
                <button
                  type="submit"
                  className="btn-primary"
                  disabled={saving}
                  style={{ height: "40px", whiteSpace: "nowrap" }}
                >
                  {saving ? "Saving…" : "Add →"}
                </button>
              </div>
            </form>
          </div>
        )}

        {/* Filter Bar */}
        <div className="filter-bar">
          <input
            className="filter-input"
            placeholder="🔍 Search item, store, notes…"
            value={filterText}
            onChange={e => setFilterText(e.target.value)}
            style={{ minWidth: "260px" }}
          />
          <select
            className="filter-input"
            value={filterCategory}
            onChange={e => setFilterCategory(e.target.value)}
          >
            <option value="">All Categories</option>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
          {(filterText || filterCategory) && (
            <button
              className="filter-clear"
              onClick={() => { setFilterText(""); setFilterCategory(""); }}
            >
              Clear filters
            </button>
          )}
        </div>

        {/* Table */}
        <div className="table-card">
          <div className="table-wrap">
            {loading ? (
              <div className="table-empty">Loading expenses…</div>
            ) : sorted.length === 0 ? (
              <div className="table-empty">
                {expenses.length === 0
                  ? "No expenses yet — add your first one above!"
                  : "No expenses match your filters."}
              </div>
            ) : (
              <table>
                <thead>
                  <tr>
                    {[
                      { key: "date",     label: "Date"     },
                      { key: "item",     label: "Item"     },
                      { key: "store",    label: "Store"    },
                      { key: "category", label: "Category" },
                      { key: "quantity", label: "Qty"      },
                      { key: "price",    label: "Price"    },
                      { key: "notes",    label: "Notes"    },
                    ].map(({ key, label }) => (
                      <th
                        key={key}
                        onClick={() => handleSort(key)}
                        style={{ cursor: "pointer", userSelect: "none" }}
                      >
                        <span style={{ display: "inline-flex", alignItems: "center", gap: "4px" }}>
                          {label}
                          <span style={{ color: sortKey === key ? "var(--green)" : "var(--muted)", fontSize: "10px" }}>
                            {sortKey === key ? (sortDir === "asc" ? "↑" : "↓") : "↕"}
                          </span>
                        </span>
                      </th>
                    ))}
                    <th></th>
                  </tr>
                </thead>
                <tbody>
                  {sorted.map(expense => (
                    editId === expense.id ? (
                      <tr key={expense.id} className="edit-row">
                        <td>
                          <input
                            type="date"
                            className={`form-input ${errors.date ? "error" : ""}`}
                            value={editForm.date}
                            onChange={ev => handleFormChange("edit", "date", ev.target.value)}
                            style={{ width: "130px" }}
                          />
                        </td>
                        <td>
                          <input
                            className={`form-input ${errors.item ? "error" : ""}`}
                            value={editForm.item}
                            onChange={ev => handleFormChange("edit", "item", ev.target.value)}
                            style={{ width: "120px" }}
                          />
                        </td>
                        <td>
                          <input
                            className="form-input"
                            value={editForm.store}
                            onChange={ev => handleFormChange("edit", "store", ev.target.value)}
                            style={{ width: "100px" }}
                          />
                        </td>
                        <td>
                          <select
                            className="form-input"
                            value={editForm.category}
                            onChange={ev => handleFormChange("edit", "category", ev.target.value)}
                            style={{ width: "120px" }}
                          >
                            <option value="">Select…</option>
                            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
                          </select>
                        </td>
                        <td>
                          <div style={{ display: "flex", gap: "4px" }}>
                            <input
                              className="form-input"
                              value={editForm.quantity}
                              onChange={ev => handleFormChange("edit", "quantity", ev.target.value)}
                              style={{ width: "48px" }}
                              placeholder="1"
                            />
                            <input
                              className="form-input"
                              value={editForm.unit}
                              onChange={ev => handleFormChange("edit", "unit", ev.target.value)}
                              style={{ width: "48px" }}
                              placeholder="kg"
                            />
                          </div>
                        </td>
                        <td>
                          <input
                            className={`form-input ${errors.price ? "error" : ""}`}
                            value={editForm.price}
                            onChange={ev => handleFormChange("edit", "price", ev.target.value)}
                            style={{ width: "80px" }}
                          />
                        </td>
                        <td>
                          <input
                            className="form-input"
                            value={editForm.notes}
                            onChange={ev => handleFormChange("edit", "notes", ev.target.value)}
                            style={{ width: "120px" }}
                          />
                        </td>
                        <td>
                          <div className="td-actions">
                            <button
                              className="btn-edit"
                              onClick={() => handleEditSave(expense.id)}
                              disabled={saving}
                            >
                              {saving ? "…" : "Save"}
                            </button>
                            <button
                              className="btn-delete"
                              onClick={() => { setEditId(null); setEditForm(null); }}
                            >
                              Cancel
                            </button>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      <tr key={expense.id}>
                        <td className="td-muted">
                          {new Date(expense.date).toLocaleDateString("en-US", {
                            month: "short", day: "numeric", year: "numeric"
                          })}
                        </td>
                        <td style={{ fontWeight: 500 }}>{expense.item}</td>
                        <td className="td-muted">{expense.store || "—"}</td>
                        <td>
                          {expense.category
                            ? <span className="td-category">{expense.category}</span>
                            : <span className="td-muted">—</span>}
                        </td>
                        <td className="td-muted">
                          {expense.quantity
                            ? `${expense.quantity}${expense.unit ? " " + expense.unit : ""}`
                            : "—"}
                        </td>
                        <td className="td-price">
                          ${parseFloat(expense.price).toFixed(2)}
                        </td>
                        <td className="td-muted">{expense.notes || "—"}</td>
                        <td>
                          <div className="td-actions">
                            <button className="btn-edit" onClick={() => startEdit(expense)}>
                              Edit
                            </button>
                            <button className="btn-delete" onClick={() => handleDelete(expense.id)}>
                              Delete
                            </button>
                          </div>
                        </td>
                      </tr>
                    )
                  ))}
                </tbody>
              </table>
            )}
          </div>

          {sorted.length > 0 && (
            <div className="table-footer">
              <span>{sorted.length} expense{sorted.length !== 1 ? "s" : ""}</span>
              <span className="table-total">
                Total: ${sorted
                  .reduce((sum, e) => sum + parseFloat(e.price || 0), 0)
                  .toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
              </span>
            </div>
          )}
        </div>
      </main>
    </div>
  );
}